import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import SelectedIcon from '@material-ui/icons/CheckCircle'
import DotIcon from '@material-ui/icons/FiberManualRecord'
import SelectableIcon from '@material-ui/icons/RadioButtonUnchecked'
import type { Variant as TypographyVariant } from '@material-ui/core/styles/createTypography'
import MuiTypography from '@material-ui/core/Typography'
import classNames from 'classnames'
import type { ReactNode } from 'react'
import React, { useEffect, useRef, useState } from 'react'
const getActivityColor = (userColor: string = '#2F4870') => userColor
import type { DropdownSelectOption } from '@paintscout/ui/components/DropdownSelect'
import Grid from '@paintscout/ui/components/Grid'
import MenuItem from '@paintscout/ui/components/MenuItem'
import Tooltip from '@paintscout/ui/components/Tooltip'
import Typography from '@paintscout/ui/components/Typography'
import { useUsers } from '../../providers/UsersProvider'
import EstimatorAvatar from '../EstimatorAvatar'

const useStyles = makeStyles<Theme, DropdownSelectMenuItemProps>((theme) => ({
  root: {
    margin: ({ searchable }) => (searchable ? theme.spacing(0.25, 1, 0.25, 2) : theme.spacing(0.25)),
    padding: ({ dense }) => (dense ? theme.spacing(1, 0.5) : theme.spacing(1, 1, 1, 1.5)),
    backgroundColor: 'transparent',
    borderRadius: theme.shape.borderRadius,
    // Hover/press feedback for UNSELECTED rows only. Guarded so touch devices don't get
    // a sticky hover highlight. Selected rows are handled by `.selected` below.
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    },
    '@media (hover: none) and (pointer: coarse)': {
      '&:active': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    }
  },
  // The real ENG-705 fix. iOS/Android keep a sticky `:hover` on the last-tapped row until
  // another is tapped, and MUI's built-in `.MuiListItem-button:hover` rule resets the
  // background to `transparent` under `@media (hover: none)` — so a freshly-selected row
  // showed its checkmark but no grey bar until a different row was tapped (#13860).
  //
  // Every prior fix wrote the override as a JSS *function* value (`({ selected }) => …`)
  // inside a nested `:hover`/`@media` block. MUI v4's makeStyles does NOT emit function
  // values in nested rules, so those fixes produced no CSS at all and never took effect.
  // This is a STATIC declaration applied via a conditional className, and `!important`
  // beats MUI's non-important hover reset regardless of specificity or stylesheet order.
  selected: {
    backgroundColor: `${theme.palette.action.hover} !important`
  },
  labelWrapper: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: ({ bold }) => (bold ? 600 : 'normal')
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '22px'
  },
  avatarContainer: {
    marginLeft: `-${theme.spacing(0.5)}px`,
    marginRight: theme.spacing(1)
  },
  icon: {
    fontSize: '1.1rem'
  },
  dot: {
    marginRight: theme.spacing(1)
  }
}))

export interface DropdownSelectMenuItemProps {
  option: DropdownSelectOption
  selected: boolean
  showSelectableIcon?: boolean
  typographyVariant?: TypographyVariant
  searchable?: boolean
  bold?: boolean
  dense?: boolean
  formatOptionLabel?: (option: DropdownSelectOption) => ReactNode
  onClick: () => void
}

export default function DropdownSelectMenuItem(props: DropdownSelectMenuItemProps) {
  const { option, selected, typographyVariant = 'body1', showSelectableIcon, formatOptionLabel, onClick } = props
  const classes = useStyles(props)
  const labelRef = useRef<HTMLElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    if (labelRef.current) {
      setIsOverflowing(labelRef.current.scrollWidth > labelRef.current.clientWidth)
    }
  }, [option.label])

  return (
    <Tooltip title={option.tooltip ?? ''} placement="right" disableWrapper>
      <MenuItem
        key={option.value}
        disabled={option.disabled}
        onClick={(e) => {
          e.stopPropagation()
          onClick()
        }}
        className={classNames(classes.root, { [classes.selected]: selected })}
        value={option.value}
      >
        <Grid
          container
          justifyContent={option.crewAvatar ? 'flex-start' : 'space-between'}
          alignItems="center"
          wrap="nowrap"
        >
          {option.crewAvatar && <AvatarContainer crew option={option} />}
          {(option.customAvatar || option.color) && <AvatarContainer option={option} />}
          {option.dotColor && !option.crewAvatar && (
            <div className={classNames(classes.iconContainer, classes.dot)}>
              <DotIcon fontSize="small" style={{ color: option.dotColor }} />
            </div>
          )}
          <Grid item xs className={classes.labelWrapper}>
            {formatOptionLabel && !!formatOptionLabel(option) ? (
              formatOptionLabel(option)
            ) : (
              <Tooltip title={isOverflowing ? option.label : ''} enterDelay={500} disableWrapper>
                <MuiTypography ref={labelRef} variant={typographyVariant} className={classes.label}>
                  {option.label}
                </MuiTypography>
              </Tooltip>
            )}
            {option.sublabel && (
              <Typography
                variant="caption"
                color={option.crewAvatar || option.sublabelMuted || option.inactive ? 'textSecondary' : 'inherit'}
              >
                {option.sublabel}{' '}
              </Typography>
            )}
            {/* A deactivated user's "Deactivated" sublabel stands in for the role. */}
            {option.role && !option.inactive && <Typography variant="caption">{option.role}</Typography>}
          </Grid>
          {(selected || showSelectableIcon) && (
            <div className={classes.iconContainer}>
              {selected && <SelectedIcon className={classes.icon} />}
              {showSelectableIcon && !selected && <SelectableIcon className={classes.icon} />}
            </div>
          )}
        </Grid>
      </MenuItem>
    </Tooltip>
  )
}

interface AvatarContainerProps {
  crew?: boolean
  option: DropdownSelectOption
}

export function AvatarContainer({ crew, option }: AvatarContainerProps) {
  const classes = useStyles({} as DropdownSelectMenuItemProps)
  const { userPictures } = useUsers()
  const safeLabel = option?.label ?? ''
  const safeSublabel = option?.sublabel ?? ''
  const [firstName = '', lastName = ''] = (crew ? safeSublabel : safeLabel).split(' ')

  return (
    <div className={classes.avatarContainer}>
      <EstimatorAvatar
        userPictures={userPictures}
        user={{
          id: option?.value,
          firstName,
          lastName
        }}
        width={crew ? '32px' : '36px'}
        height={crew ? '32px' : '36px'}
        fontSize="0.9rem"
        style={
          crew
            ? {
                marginLeft: '8px'
              }
            : {}
        }
        avatarColor={crew ? option?.dotColor : getActivityColor(option?.color)}
        active={!option?.inactive}
        hideTooltip
      />
    </div>
  )
}
